import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ParamTableProps } from "../interfaces";

export const ParamTable: FC<ParamTableProps> = (props) => {
  const { rows, nameLabelKey = "parameter" } = props;
  const { t } = useTranslation();

  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-400">{t("docs.common.noParameters")}</p>
    );
  }

  // YOSHI-10464: the required column exists only for rows that answer the question. A response
  // field is not something the caller decides, so the glossary drops the column instead of filling
  // it with a dash the reader would have to interpret.
  const hasRequiredColumn: boolean = rows.some((row) => {
    return row.isRequired !== null;
  });

  return (
    // The table scrolls on its own on a phone instead of forcing the page sideways.
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[34rem] text-left text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            <th scope="col" className="py-2 pr-4 font-semibold text-gray-700">
              {t(`docs.common.${nameLabelKey}`)}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold text-gray-700">
              {t("docs.common.type")}
            </th>
            {hasRequiredColumn && (
              <th scope="col" className="py-2 pr-4 font-semibold text-gray-700">
                {t("docs.common.required")}
              </th>
            )}
            <th scope="col" className="py-2 font-semibold text-gray-700">
              {t("docs.common.description")}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* YOSHI-10464: the name cell never wraps. `break-all` split identifiers mid-word on a
              phone (`order_sourc` / `e`), which reads as a different field; the table already
              scrolls sideways inside its own box, so letting the name widen it costs nothing. */}
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-gray-100 align-top">
              {/* The block this table belongs to already states the location, so the row does not
                  repeat it. */}
              <td className="py-3 pr-4">
                <code className="font-mono text-xs sm:text-sm text-primary whitespace-nowrap">
                  {row.name}
                </code>
              </td>
              <td className="py-3 pr-4 font-mono text-xs text-gray-500">
                {row.type}
              </td>
              {hasRequiredColumn && (
                <td className="py-3 pr-4 text-xs text-gray-500">
                  {row.isRequired ? t("docs.common.yes") : t("docs.common.no")}
                </td>
              )}
              <td className="py-3 text-gray-500 leading-relaxed">
                {t(row.descriptionKey)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
